import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { Section, SectionTitle } from '@/features/app-shell/section'
import { reportQueries } from '@/features/report/queries'

import { getCurrentTenant } from '@/shared/auth/store'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { Skeleton } from '@/components/ui/skeleton'

const ReportContent = lazy(
	() => import('@/features/report/components/report-content'),
)

const searchSchema = z.object({
	start_time: z.string().optional(),
	end_time: z.string().optional(),
})

export const Route = createFileRoute('/_dashboard/report/graylog')({
	validateSearch: zodValidator(searchSchema),

	loaderDeps: ({ search: { start_time, end_time } }) => {
		return { start_time, end_time }
	},

	loader: async ({
		deps: { start_time, end_time },
		context: { queryClient },
	}) => {
		const tenant = getCurrentTenant() as string

		if (tenant) {
			await queryClient.prefetchQuery(
				reportQueries.graylogReport(tenant, start_time, end_time),
			)
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Graylog Reports | BitLyft AIR®',
			},
		],
	}),

	component: ReportRoute,
	pendingComponent: ReportPending,
	errorComponent: RouteErrorComponent,
})

function ReportRoute() {
	return (
		<ErrorBoundary>
			<Suspense fallback={<ReportSkeleton />}>
				<ReportContent />
			</Suspense>
		</ErrorBoundary>
	)
}

function ReportSkeleton() {
	return (
		<div className="flex flex-col items-start justify-start gap-4">
			<Skeleton className="h-[170px] w-full" />
			<Skeleton className="h-[170px] w-full" />
			<Skeleton className="h-[170px] w-full" />
		</div>
	)
}

function ReportPending() {
	return (
		<Section>
			<SectionTitle>Report</SectionTitle>

			<ReportSkeleton />
		</Section>
	)
}
